import { isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo, useRef } from 'react';
import { Options } from 'react-select';

import { useAppSelector } from '../../../../hooks/react-redux';
import { getAllDropdownLists } from '../../../admin/dropdown-lists/store';
import { DropdownOption, Dropdown as SharedDropdown } from '../../../shared/dropdown/Dropdown';
import { SignOffConversationIcon } from '../sign-off';
import { SignOffConversation } from '../store';
import styles from './Fields.module.scss';
import { fieldBoxShadow } from '../../../constants/style';

const { amethyst, primary, disabledGrey } = styles;
interface DropdownProps {
    id: string;
    value: string[] | null;
    updateValue: (value: string[] | null) => void;
    dropdownList: string;
    isMulti?: boolean;
    disabled?: boolean;
    showFieldUpdated?: boolean;
    menuPortalTarget?: HTMLElement | null;
    hideDropdownIndicator?: boolean;
    signOffConversation?: SignOffConversation;
}

export const Dropdown: React.FC<DropdownProps> = ({
    id,
    value,
    updateValue,
    dropdownList,
    disabled = false,
    isMulti = false,
    showFieldUpdated = false,
    menuPortalTarget = document.body,
    hideDropdownIndicator = false,
    signOffConversation
}) => {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dropdownLists = useAppSelector(getAllDropdownLists);

    const mapDropdownOptions = useCallback((options: string[]) => options.map(value => ({ label: value, value, disabled })), [disabled]);
    const options = useMemo(() => mapDropdownOptions(dropdownLists.find(({ name }) => name === dropdownList)?.options || []), [dropdownLists, dropdownList, mapDropdownOptions]);

    const updateDropdownValue = useCallback((option: DropdownOption | Options<DropdownOption> | null) => {
        let value = null;
        if (!isNull(option)) {
            if (isMulti) {
                value = (option as Options<DropdownOption>).map(({ value }) => value);
            } else {
                value = [(option as DropdownOption).value];
            }
        }
        updateValue(value);
    }, [updateValue, isMulti]);

    const mappedValue = useMemo(() => options.filter(option => value?.includes(option.value)) || null, [value, options]);

    const MAX_MENU_HEIGHT = 150;

    const controlBorderColor = useMemo(() => {
        if (showFieldUpdated) {
            return amethyst;
        }
        return disabled ? disabledGrey : primary;
    }, [showFieldUpdated, disabled]);

    const controlBoxShadow = useMemo(() => showFieldUpdated ? fieldBoxShadow(amethyst) : undefined, [showFieldUpdated]);

    const dropdownWidth = useMemo(() => !isUndefined(signOffConversation) ? 'calc(100% - 42px)' : '100%', [signOffConversation]);

    const dropdownComponent = useMemo(() => (
        <div
            data-testid={`opinion-dropdown-${id}`}
            className={styles.dropdownFieldWrapper}
            ref={dropdownRef}
            style={{ maxWidth: '400px', width: dropdownWidth }}
        >
            <SharedDropdown
                disabled={disabled}
                value={mappedValue}
                onChange={updateDropdownValue}
                isMulti={isMulti}
                options={options}
                controlBorderColor={controlBorderColor}
                controlBoxShadow={controlBoxShadow}
                maxMenuHeight={MAX_MENU_HEIGHT}
                menuPlacement='auto'
                menuPortalTarget={menuPortalTarget}
                isClearable
                hideDropdownIndicator={hideDropdownIndicator}
                minControlHeight='38px'
                fontWeight={500}
            />
        </div>
    ), [disabled, mappedValue, updateDropdownValue, isMulti, options, controlBorderColor, controlBoxShadow, menuPortalTarget, hideDropdownIndicator, id, dropdownWidth]);

    if (!isUndefined(signOffConversation)) {
        return (
            <div className={styles.dropdownAndNotesWrapper}>
                {dropdownComponent}
                <SignOffConversationIcon signOffConversation={signOffConversation} id={id} />
            </div>
        );
    }

    return dropdownComponent;
};
